import React, { useEffect, useState } from "react";
import Breadcrumb from "../components/Breadcrumb";
import LoadingComponent from "../components/ui/LoadingComponent";
import { api } from "../Apis/Api";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import ErrorAlert from "../components/ui/ErrorAlert";

const SubscriptionPage = () => {
  const [error, setError] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // Fetch current subscription info from get-dashboard-counts API
  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      try {
        setError(true)
        const today = new Date().toISOString().split("T")[0];
        const response = await api.get(
          `/get-client-plan-status`
        );

        if (response.data.success && response.data.data && response.data.data.plan_id) {
          const data = response.data.data;
          setCurrentSubscription({
            planId: data.plan_id,
            planName: data.plan_name,
            startDate: data.subscription_start_date || null, // Fallback to null if not provided
            endDate: data.subscription_end_date,
            isExpired: data.isExpired || false,
            is_plan_fully_used: data.is_plan_fully_used || false,
            maxVehiclesAllowed: data.max_vehicles_allowed || 0,
          });
        } else {
          setCurrentSubscription(null); // No valid subscription data
        }
      } catch (error) {
        // console.error("Error fetching subscription info:", error);
        // toast.error("Failed to fetch subscription information");
        setError(true)
        setCurrentSubscription(null);
      }
    };

    fetchCurrentSubscription();
  }, []);

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setError(null)
        setLoading(true);
        const response = await api.get("/get-all-pricing-plans");
        if (response.data.success && Array.isArray(response.data.data)) {
          const nonTrialPlans = response.data.data.filter(
            (plan) => !plan.is_trial_plan
          );
          setPlans(nonTrialPlans);
        } else {
          throw new Error("Invalid response format from API");
        }
      } catch (error) {
        // console.error("Fetch Plans API Error:", error);
        // toast.error(error.message || "Failed to fetch pricing plans");
        setError(true)
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle plan selection and Stripe checkout
  const handlePlanSelect = async (plan) => {
    try {
      const response = await api.post("/stripe/create-checkout-session", {
        plan_id: plan.plan_id,
      });
      if (response.data.success && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error("Invalid response from checkout API");
      }
    } catch (error) {
      console.error("Stripe Checkout API Error:", error);
      toast.error(
        error.response?.data?.message || "Failed to initiate checkout"
      );
    }
  };

  // Format price and fees for display
  const formatPrice = (basePrice, perVehicleFee, planType, volumeThreshold) => {
    if (volumeThreshold === 0) {
      return `$${parseFloat(basePrice).toFixed(2)}/mo`;
    } else {
      return `$${parseFloat(basePrice).toFixed(2)}/mo + $${parseFloat(
        perVehicleFee
      ).toFixed(2)} after ${volumeThreshold} vehicles`;
    }
  };

  if (loading) {
    return (
      <>
        <Breadcrumb title="Subscription" />
        <LoadingComponent />
      </>
    );
  }

  if(error){
    return <ErrorAlert/>
  }
  return (
    <>
      <style>
        {`
          .subscription-page .subscription-container {
            padding: var(--size-32) var(--size-16); /* 2rem 1rem */
            width: 100%;
            min-height: 100vh;
            margin: 0 auto;
            box-sizing: border-box;
          }

          .subscription-page .current-subscription-info {
            border-radius: var(--rounded-16); /* 1rem */
            padding: var(--size-24); /* 1.5rem */
            margin-bottom: var(--size-32); /* 2rem */
            box-shadow: var(--shadow-4);
            border: 2px solid var(--primary-200);
          }
          .subscription-page .current-subscription-info .info-header {
            display: flex;
            align-items: center;
            gap: var(--size-12); /* 0.75rem */
            margin-bottom: var(--size-16); /* 1rem */
          }
          .subscription-page .current-subscription-info .info-header .info-icon {
            font-size: var(--font-2xl); /* 1.75rem */
            color: var(--primary-600);
          }
          .subscription-page .current-subscription-info .info-header h3 {
            font-size: var(--font-xl); /* 1.25rem */
            font-weight: 600;
            color: var(--neutral-800);
            margin: 0;
          }
          .subscription-page .current-subscription-info .info-details {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: var(--size-16); /* 1rem */
          }
          .subscription-page .current-subscription-info .info-item {
            display: flex;
            flex-direction: column;
            gap: var(--size-4); /* 0.25rem */
          }
          .subscription-page .current-subscription-info .info-item .label {
            font-size: var(--font-sm); /* 0.875rem */
            color: var(--text-secondary-light);
            font-weight: 500;
          }
          .subscription-page .current-subscription-info .info-item .value {
            font-size: var(--font-md); /* 1rem */
            color: var(--neutral-800);
            font-weight: 600;
          }
          .subscription-page .current-subscription-info .info-item .value.status {
            display: inline-block;
            padding: var(--size-4) var(--size-12); /* 0.25rem 0.75rem */
            border-radius: var(--rounded-8); /* 0.5rem */
            font-size: var(--font-sm); /* 0.875rem */
          }
          .subscription-page .current-subscription-info .info-item .value.status.active {
            color: var(--success-700);
          }
          .subscription-page .current-subscription-info .info-item .value.status.active span {
            background: var(--success-200);
          }
          .subscription-page .current-subscription-info .info-item .value.status.expired {
            color: var(--error-700);
          }
          .subscription-page .current-subscription-info .info-item .value.status.expired span {
            background: var(--error-200);
          }

          .subscription-page .subscription-header {
            text-align: center;
            margin-bottom: var(--size-32); /* 2rem */
          }
          .subscription-page .subscription-header h2 {
            font-size: var(--font-2xl); /* 1.75rem */
            font-weight: 600;
            color: var(--neutral-800);
            margin-bottom: var(--size-8); /* 0.5rem */
          }
          .subscription-page .subscription-header p {
            font-size: var(--font-md); /* 1rem */
            color: var(--text-secondary-light);
          }
          .subscription-page .plans-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: var(--size-24); /* 1.5rem */
            align-items: stretch;
            width: 100%;
          }
          .subscription-page .plan-card {
            background: var(--white);
            border-radius: var(--rounded-16); /* 1rem */
            padding: var(--size-24); /* 1.5rem */
            text-align: left;
            transition: background-color 0.3s, color 0.3s, border 0.3s;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: 100%;
            min-height: 400px;
            width: 100%;
            box-sizing: border-box;
            border: 2px solid var(--border-card);
          }
          .subscription-page .plan-card.current-plan {
            border: 2px solid var(--primary-600);
          }
          .subscription-page .plan-card .plan-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: var(--size-8); /* 0.5rem */
            flex-wrap: wrap;
            gap: var(--size-8);
          }
          .subscription-page .plan-card .current-badge {
            display: inline-flex;
            align-items: center;
            gap: var(--size-4); /* 0.25rem */
            padding: var(--size-4) var(--size-12); /* 0.25rem 0.75rem */
            background: var(--primary-100);
            color: var(--primary-700);
            border-radius: var(--rounded-8); /* 0.5rem */
            font-size: var(--font-xs); /* 0.75rem */
            font-weight: 600;
          }
          .subscription-page .plan-card:hover {
            background-color: var(--primary-400);
            color: var(--white);
          }
          .subscription-page .plan-card:hover h3,
          .subscription-page .plan-card:hover .price,
          .subscription-page .plan-card:hover .segment,
          .subscription-page .plan-card:hover ul li,
          .subscription-page .plan-card:hover .description {
            color: var(--white);
          }
          .subscription-page .plan-card:hover ul li .icon {
            color: var(--light-600);
          }
          .subscription-page .plan-card:hover .current-badge {
            background: var(--white);
            color: var(--primary-700);
          }
          .subscription-page .plan-card h3 {
            font-size: var(--font-xxl); /* 1.5rem */
            font-weight: 600;
            color: var(--neutral-800);
            margin: 0;
          }
          .subscription-page .plan-card .price {
            font-size: var(--font-2xl); /* 1.75rem */
            font-weight: bold;
            color: var(--primary-700);
            margin-bottom: var(--size-8); /* 0.5rem */
          }
          .subscription-page .plan-card .segment {
            font-size: var(--font-sm); /* 0.875rem */
            color: var(--text-secondary-light);
            margin-bottom: var(--size-16); /* 1rem */
            text-transform: capitalize;
          }
          .subscription-page .plan-card .description {
            font-size: var(--font-sm); /* 0.875rem */
            color: var(--text-secondary-light);
            margin-bottom: var(--size-16); /* 1rem */
          }
          .subscription-page .plan-card ul {
            list-style: none;
            padding: 0;
            margin-bottom: var(--size-24); /* 1.5rem */
            flex-grow: 1;
          }
          .subscription-page .plan-card ul li {
            font-size: var(--font-sm); /* 0.875rem */
            color: var(--text-secondary-light);
            margin-bottom: var(--size-8); /* 0.5rem */
            display: flex;
            align-items: flex-start;
            gap: var(--size-8); /* 0.5rem */
          }
          .subscription-page .plan-card ul li .icon {
            color: var(--primary-700);
            font-size: var(--font-md); /* 1rem */
          }
          .subscription-page .plan-card .select-button {
            display: inline-flex;
            align-items: center;
            gap: var(--size-8); /* 0.5rem */
            padding: var(--size-12) var(--size-24); /* 0.75rem 1.5rem */
            background: var(--primary-700);
            color: white;
            border: none;
            border-radius: var(--rounded-8); /* 0.5rem */
            font-size: var(--font-md); /* 1rem */
            font-weight: 500;
            cursor: pointer;
            transition: background 0.3s;
            align-self: center;
          }
          .subscription-page .plan-card:hover .select-button {
            background: var(--primary-900);
          }
          .subscription-page .plan-card .select-button:disabled {
            background: var(--primary-100);
            cursor: not-allowed;
            opacity: 0.7;
          }
          .subscription-page .no-plans-container {
            text-align: center;
            padding: var(--size-32); /* 2rem */
            font-size: var(--font-md); /* 1rem */
            color: var(--text-secondary-light);
            background: var(--white);
            border-radius: var(--rounded-12); /* 0.75rem */
            border: 2px dashed var(--neutral-300);
          }

          @media (max-width: 1024px) {
            .subscription-page .subscription-container {
              padding: var(--size-24) var(--size-16);
            }
            .subscription-page .current-subscription-info .info-details {
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            }
            .subscription-page .plans-grid {
              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
              gap: var(--size-16);
            }
          }

          @media (max-width: 768px) {
            .subscription-page .subscription-container {
              padding: var(--size-16) var(--size-12);
            }
            .subscription-page .current-subscription-info {
              padding: var(--size-16);
            }
            .subscription-page .current-subscription-info .info-details {
              grid-template-columns: 1fr;
              gap: var(--size-12);
            }
            .subscription-page .subscription-header h2 {
              font-size: var(--font-xl);
            }
            .subscription-page .subscription-header p {
              font-size: var(--font-sm);
            }
            .subscription-page .plans-grid {
              grid-template-columns: 1fr;
              gap: var(--size-16);
            }
            .subscription-page .plan-card {
              padding: var(--size-16);
              min-height: auto;
            }
            .subscription-page .plan-card h3 {
              font-size: var(--font-xl);
            }
            .subscription-page .plan-card .price {
              font-size: var(--font-xxl);
            }
          }

          @media (max-width: 480px) {
            .subscription-page .subscription-container {
              padding: var(--size-12) var(--size-8);
            }
            .subscription-page .subscription-header {
              margin-bottom: var(--size-24);
            }
            .subscription-page .subscription-header h2 {
              font-size: var(--font-xl);
            }
            .subscription-page .subscription-header p {
              font-size: var(--font-xs);
            }
            .subscription-page .plan-card {
              padding: var(--size-12);
            }
            .subscription-page .plan-card ul li {
              font-size: var(--font-xs);
            }
            .subscription-page .plan-card .select-button {
              width: 100%;
              justify-content: center;
            }
          }

          @media (max-width: 360px) {
            .subscription-page .subscription-container {
              padding: var(--size-8) var(--size-4);
            }
            .subscription-page .plans-grid {
              gap: var(--size-12);
            }
          }

          @media (min-width: 1400px) {
            .subscription-page .subscription-container {
              padding: var(--size-32) var(--size-32);
            }
            .subscription-page .plans-grid {
              grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
              gap: var(--size-32);
            }
          }

          @media (min-width: 1920px) {
            .subscription-page .subscription-container {
              padding: var(--size-32) var(--size-48);
            }
            .subscription-page .plans-grid {
              grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
              gap: var(--size-40);
            }
          }
        `}
      </style>

      <Breadcrumb title="Subscription" />
      <div className="subscription-page">
        <div className="subscription-container">
          <div className="subscription-header">
            <h2>Choose Your Subscription Plan</h2>
            <p>
              Select a plan that best fits your needs to continue using Park
              Vision.
            </p>
          </div>

          {/* Current Subscription Info - Only show if currentSubscription exists and is valid */}
          {currentSubscription && !currentSubscription.isExpired && (
            <div className="current-subscription-info">
              <div className="info-header">
                <Icon icon="mdi:information-outline" className="info-icon" />
                <h3>Current Subscription</h3>
              </div>
              <div className="info-details">
                <div className="info-item">
                  <span className="label">Plan:</span>
                  <span className="value">{currentSubscription.planName}</span>
                </div>
                <div className="info-item">
                  <span className="label">Start Date:</span>
                  <span className="value">
                    {formatDate(currentSubscription.startDate)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">End Date:</span>
                  <span className="value">
                    {formatDate(currentSubscription.endDate)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span
                    className={`value status ${
                      currentSubscription.isExpired ? "expired" : "active"
                    }`}
                  >
                    <span className="px-3 py-2 rounded-5">
                      {currentSubscription.isExpired ? "Expired" : "Active"}
                    </span>
                  </span>
                </div>
               
              </div>
            </div>
          )}

          {/* If subscription is expired, show a separate expired message */}
          {/* {currentSubscription && currentSubscription.isExpired && (
            <div className="current-subscription-info" style={{ borderColor: 'var(--error-200)', backgroundColor: 'var(--error-50)' }}>
              <div className="info-header">
                <Icon icon="mdi:alert-circle-outline" className="info-icon" style={{ color: 'var(--error-600)' }} />
                <h3>Subscription Expired</h3>
              </div>
              <div className="info-details">
                <div className="info-item">
                  <span className="label">Plan:</span>
                  <span className="value">{currentSubscription.planName}</span>
                </div>
                <div className="info-item">
                  <span className="label">End Date:</span>
                  <span className="value">
                    {formatDate(currentSubscription.endDate)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className="value status expired">
                    <span className="px-3 py-2 rounded-5">Expired</span>
                  </span>
                </div>
                <p style={{ marginTop: '1rem', color: 'var(--error-700)', fontWeight: 500 }}>
                  Please select a new plan to continue using Park Vision.
                </p>
              </div>
            </div>
          )} */}

          {plans.length === 0 ? (
            <div className="no-plans-container">
              <p>No plans available at the moment.</p>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map((plan) => {
                // const isCurrentPlan =
                //   currentSubscription &&
                //   plan.plan_id === currentSubscription.planId &&
                //   !currentSubscription.isExpired;

                const isCurrentPlan =
  currentSubscription &&
  plan.plan_id === currentSubscription.planId &&
  !currentSubscription.isExpired;

  const isRenewable = 
  currentSubscription &&
  plan.plan_id === currentSubscription.planId &&
  currentSubscription.is_plan_fully_used &&
  !currentSubscription.isExpired;
                return (
                  <div
                    key={plan.plan_id}
                    className={`plan-card ${
                      isCurrentPlan ? "current-plan" : ""
                    }`}
                  >
                    <div>
                      <div className="plan-header">
                        <h3>{plan.plan_name}</h3>
                        {isCurrentPlan && (
                          <span className="current-badge">
                            <Icon icon="mdi:check-circle" />
                            Current Plan
                          </span>
                        )}
                      </div>
                      <div className="price">
                        {formatPrice(
                          plan.base_price,
                          plan.per_vehicle_fee,
                          plan.plan_type,
                          plan.volume_threshold
                        )}
                      </div>
                      <div className="segment">
                        For {plan.customer_segment} Businesses
                      </div>
                      <div className="description">{plan.description}</div>
                      <ul>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Plan Type:{" "}
                          {plan.plan_type.charAt(0).toUpperCase() +
                            plan.plan_type.slice(1)}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Max Vehicles: {plan.max_vehicles_allowed}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Vehicle Range: {plan.min_vehicle_count} -{" "}
                          {plan.max_vehicle_count}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Volume Threshold: {plan.volume_threshold || "-"}
                        </li>
                        <li>
                          <Icon icon="mdi:check-circle" className="icon" />
                          Status: {plan.is_active ? "Active" : "Inactive"}
                        </li>
                      </ul>
                    </div>
                    <button
                      className="select-button"
                      onClick={() => handlePlanSelect(plan)}
                      // disabled={loading || !plan.is_active || isCurrentPlan}
                      disabled={
  loading || 
  !plan.is_active || 
  (isCurrentPlan && !isRenewable)   // sirf tab disable jab active hai aur fully used nahi
}
                    >
                      {/* {isCurrentPlan ? (
                        <>
                          <Icon icon="mdi:check-circle" />
                          Selected
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:cart" />
                          Select Plan
                        </>
                      )} */}

                      {isRenewable ? (
  <>
    <Icon icon="mdi:refresh" />
    Renew Plan
  </>
) : isCurrentPlan ? (
  <>
    <Icon icon="mdi:check-circle" />
    Selected
  </>
) : (
  <>
    <Icon icon="mdi:cart" />
    Select Plan
  </>
)}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionPage;